import React from 'react';
import { formatTestCardId } from '../../../utils/converter';
import { STEP_LABELS, fmtDay, hasReachedStep, liveProgress, structureOf } from '../epmHelpers';
import { progressOf } from './stepData';

// What each later step holds, from the storyboard annotations (04–06)
const STEP_SUMMARIES = {
    3: {
        subtitle: 'Build the variants, prove them in staging and production, and record a real sign-off before any traffic is touched.',
        holds: ['Variants bound to Stride Experiences, with screenshots', 'Staging and production checklists from the org template', 'A production sign-off by someone other than the builder'],
    },
    4: {
        subtitle: 'Set the go-live and end rule, prefilled so the usual case is a single click.',
        holds: ['Go-live date and time from the proposed slot', 'The end rule: fixed sample, fixed date or sequential', 'A conflict check against every live and scheduled test'],
    },
    5: {
        subtitle: 'Read the result honestly, record a verdict, and write down what we now believe.',
        holds: ['Validity checks before any number is shown', 'The primary metric with its confidence interval', 'The verdict, and the learning that feeds the next idea'],
    },
};

const StepNotReached = ({ stepIndex, testData }) => {
    const summary = STEP_SUMMARIES[stepIndex];
    const reached = hasReachedStep(testData, stepIndex);
    const id = formatTestCardId(testData);
    // A live test's readout stays closed until it ends: no peeking
    const live = testData?.status === 'LIVE' && stepIndex === 5;
    const progress = live ? (liveProgress(testData) || progressOf(testData)) : null;
    const endDay = structureOf(testData).schedule?.end_day;
    const proposedStart = stepIndex === 4 ? structureOf(testData).schedule?.start_day : undefined;

    const headline = () => {
        if (live) return `Live · day ${progress.day} of ${progress.total} — the readout unlocks when the test ends (no peeking).`;
        if (reached) return `Nothing has been recorded for ${id} at this step yet.`;
        return `Fills in once ${id} reaches ${STEP_LABELS[stepIndex]}.`;
    };

    return (
        <div className="epm-plan">
            <header className="epm-workflow-step__heading epm-plan__heading">
                <span>{String(stepIndex + 1).padStart(2, '0')}</span>
                <div>
                    <h2>{STEP_LABELS[stepIndex]}</h2>
                    <p>{summary.subtitle}</p>
                </div>
            </header>

            <section className="epm-plan__card">
                <p className="text-[15px] font-semibold text-stride-heading m-0 mb-2">{headline()}</p>
                {live && (
                    <p className="text-[13px] text-stride-muted m-0 mb-4">
                        {Number.isFinite(endDay) ? `Ends ${fmtDay(endDay, 'ddd D MMM')}, then 48 hours for Adobe Analytics to settle.` : 'Then 48 hours for Adobe Analytics to settle.'}
                        {' '}Reading a fixed-horizon test before its planned end inflates false wins.
                    </p>
                )}
                {Number.isFinite(proposedStart) && (
                    <p className="text-[13px] text-stride-muted m-0 mb-4">Proposed slot: go-live {fmtDay(proposedStart, 'ddd D MMM')}, from the Schedule page.</p>
                )}
                <p className="text-[13px] text-stride-muted m-0 mb-4">This step holds:</p>
                <ul className="m-0 pl-[18px] text-[13.5px] text-stride-body list-disc">
                    {summary.holds.map((item) => <li key={item} className="my-1.5">{item}</li>)}
                </ul>
            </section>
        </div>
    );
};

export default StepNotReached;
